import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import "./email-verification-banner.css";

const REQUEST_TIMEOUT_MS = 15000;

async function fetchWithTimeout(url, options = {}, timeoutMs = REQUEST_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

function EmailVerificationBanner() {
  const { t } = useLanguage();
  const tr = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const token = localStorage.getItem("token");
  const [email, setEmail] = useState("");
  const [isVerified, setIsVerified] = useState(true);
  const [statusLoading, setStatusLoading] = useState(true);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const apiHeaders = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  useEffect(() => {
    if (!token) {
      setStatusLoading(false);
      setIsVerified(true);
      return;
    }

    let canceled = false;

    const loadStatus = async () => {
      try {
        const res = await fetchWithTimeout("/api/auth/email-verification/status", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (canceled) return;

        if (!res.ok) {
          setIsVerified(true);
          setStatusLoading(false);
          return;
        }

        setEmail(data.email || "");
        setIsVerified(Boolean(data.verified));
        setCooldown(Number(data.canResendInSeconds || 0));
        setStatusLoading(false);
      } catch (_) {
        if (!canceled) {
          setIsVerified(true);
          setStatusLoading(false);
        }
      }
    };

    loadStatus();

    return () => {
      canceled = true;
    };
  }, [token]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  if (statusLoading || isVerified || !token) return null;

  const handleResend = async () => {
    setError("");
    setSuccess("");
    setIsResending(true);
    try {
      const res = await fetchWithTimeout("/api/auth/email-verification/resend", {
        method: "POST",
        headers: apiHeaders,
      });
      const data = await res.json();

      if (!res.ok) {
        const fallbackMsg = tr("emailVerificationBanner.resendFailed", "Unable to resend verification code right now.");
        const codeSuffix = data?.code ? ` (${data.code})` : "";
        setError(`${data.message || fallbackMsg}${codeSuffix}`);
        setCooldown(Number(data.retryAfterSeconds || cooldown || 0));
        setIsResending(false);
        return;
      }

      setSuccess(tr("emailVerificationBanner.codeResent", "A new verification code has been sent."));
      setCooldown(60);
    } catch (error) {
      if (error?.name === "AbortError") {
        setError(tr("emailVerificationBanner.requestTimeout", "Request timed out. Please try again."));
      } else {
        setError(tr("emailVerificationBanner.networkError", "Network error. Please try again."));
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError(tr("emailVerificationBanner.invalidCode", "Please enter a valid 6-digit code."));
      return;
    }

    setIsVerifying(true);
    try {
      const res = await fetchWithTimeout("/api/auth/email-verification/verify", {
        method: "POST",
        headers: apiHeaders,
        body: JSON.stringify({ code: trimmed }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || tr("emailVerificationBanner.verifyFailed", "Unable to verify code."));
        setIsVerifying(false);
        return;
      }

      if (data.token) {
        localStorage.setItem("token", data.token);
      }
      if (data.role) {
        localStorage.setItem("role", data.role);
      }
      if (typeof data.firstname === "string") {
        localStorage.setItem("firstname", data.firstname);
      }
      if (typeof data.lastname === "string") {
        localStorage.setItem("lastname", data.lastname);
      }
      localStorage.setItem("emailVerified", "true");
      setSuccess(tr("emailVerificationBanner.verified", "Email verified successfully."));
      setIsVerified(true);
    } catch (error) {
      if (error?.name === "AbortError") {
        setError(tr("emailVerificationBanner.requestTimeout", "Request timed out. Please try again."));
      } else {
        setError(tr("emailVerificationBanner.networkError", "Network error. Please try again."));
      }
      setIsVerifying(false);
    }
  };

  return (
    <div className="email-verification-banner" role="region" aria-label="email verification notice">
      <div className="banner-top">
        <div>
          <p className="banner-title mb-1">{tr("emailVerificationBanner.title", "Verify Your Email")}</p>
          <p className="banner-subtitle mb-0">
            {tr("emailVerificationBanner.subtitle", "For account security, please verify your email to unlock full trust features.")} {email ? `(${email})` : ""}
          </p>
        </div>
      </div>

      <form className="banner-form" onSubmit={handleVerify}>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          className="form-control"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder={tr("emailVerificationBanner.codePlaceholder", "Enter 6-digit code")}
          required
        />
        <button className="btn btn-primary" type="submit" disabled={isVerifying}>
          {isVerifying ? tr("emailVerificationBanner.verifying", "Verifying...") : tr("emailVerificationBanner.verifyBtn", "Verify")}
        </button>
        <button
          className="btn btn-light"
          type="button"
          onClick={handleResend}
          disabled={isResending || cooldown > 0}
        >
          {isResending
            ? tr("emailVerificationBanner.resending", "Sending...")
            : cooldown > 0
              ? tr("emailVerificationBanner.resendIn", `Resend in ${cooldown}s`).replace("{{seconds}}", String(cooldown))
              : tr("emailVerificationBanner.resendBtn", "Resend Code")}
        </button>
      </form>

      {error && <div className="text-danger small mt-2">{error}</div>}
      {success && <div className="text-success small mt-2">{success}</div>}
    </div>
  );
}

export default EmailVerificationBanner;
